import { ReactNode, memo } from 'react';

/**
 * Column definition for the generic table
 */
export interface Column<T> {
  /** Unique key for the column */
  key: string;
  /** Header label to display */
  header: string;
  /** Optional custom className for the column cells */
  className?: string;
  /** Optional custom className for the header cell */
  headerClassName?: string;
  /** Custom render function for cell content */
  render?: (row: T, index: number) => ReactNode;
  /** If render is not provided, use this accessor to get the value from row */
  accessor?: keyof T | ((row: T) => ReactNode);
}

/**
 * Props for the generic Table component
 */
export interface TableProps<T> {
  /** Array of column definitions */
  columns: Column<T>[];
  /** Data rows to display */
  data: T[];
  /** Optional className for the table wrapper */
  className?: string;
  /** Optional unique key extractor function */
  keyExtractor?: (row: T, index: number) => string | number;
  /** Optional no data message */
  emptyMessage?: string;
  /** Table variant */
  variant?: 'default' | 'striped' | 'bordered';
  /** Enable hover effect on rows */
  hoverable?: boolean;
}

/**
 * Generic reusable Table component
 * Pure component optimized with memo for performance
 */
function TableComponent<T>(props: TableProps<T>) {
  const {
    columns,
    data,
    className = '',
    keyExtractor = (_, index) => index,
    emptyMessage = 'No data available',
    variant = 'default',
    hoverable = true,
  } = props;

  const getRowClassName = () => {
    const base = 'border-b border-gray-200 last:border-b-0';
    const hover = hoverable ? 'hover:bg-gray-50' : '';
    const striped = variant === 'striped' ? 'even:bg-gray-50' : '';
    return `${base} ${hover} ${striped} transition-colors duration-150`;
  };

  const tableClassName = variant === 'bordered' 
    ? 'border border-gray-200 rounded-lg overflow-hidden'
    : '';

  return (
    <div className={`overflow-x-auto ${className}`} style={{ overflow: 'visible' }}>
      <table className={`min-w-full divide-y divide-gray-200 ${tableClassName}`}>
        <thead className="bg-gray-50">
          <tr>
            {columns.map((column) => (
              <th
                key={column.key}
                className={`
                  px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider
                  ${column.headerClassName || ''}
                `}
              >
                {column.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {data.length === 0 ? (
            <tr>
              <td
                colSpan={columns.length}
                className="px-4 py-8 text-center text-sm text-gray-500"
              >
                {emptyMessage}
              </td>
            </tr>
          ) : (
            data.map((row, rowIndex) => (
              <tr key={keyExtractor(row, rowIndex)} className={getRowClassName()}>
                {columns.map((column) => {
                  let cellContent: ReactNode;

                  if (column.render) {
                    cellContent = column.render(row, rowIndex);
                  } else if (column.accessor) {
                    cellContent = typeof column.accessor === 'function'
                      ? column.accessor(row)
                      : String(row[column.accessor] ?? '');
                  } else {
                    cellContent = null;
                  }

                  return (
                    <td
                      key={column.key}
                      className={`px-4 py-3 text-sm text-gray-900 ${column.className || ''}`}
                      style={column.key === 'actions' ? { overflow: 'visible' } : undefined}
                    >
                      {cellContent}
                    </td>
                  );
                })}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Memoized Table component for optimal performance
 * Only re-renders when props actually change
 */
export const Table = memo(TableComponent) as typeof TableComponent;
